import { useEffect, useRef, useCallback, useState } from "react";
import { X, Loader2, ArrowLeft } from "lucide-react";
import useChatThreads from "../Center/Chat/hooks/useChatThreads";
import useChatStream from "../Center/Chat/hooks/useChatStream";
import ChatThread from "../Center/Chat/thread/ChatThread";
import renderPrivateThread from "../Center/Chat/thread/PrivateThread";
import ChatEntry from "../Center/Chat/input/ChatEntry";
import ArticleDetailPanel from "../Universal/ArticleCardDetailPanel";
import { getDocIdData } from "../../api/universal";
import { getUserUuid, isUserInArray } from "../Center/Chat/utils/auth";

const SidebarAgentChat = ({ agent = "WIKI" }) => {
  const hasCreatedThread = useRef(false);

  /* ── Source overlay panel state ── */
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceError, setSourceError] = useState(null);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [sourceArticles, setSourceArticles] = useState([]);
  const [activeSourceId, setActiveSourceId] = useState(null);
  const [citationInteractions, setCitationInteractions] = useState({});
  const citationInteractionsRef = useRef(citationInteractions);
  const userUuidRef = useRef(getUserUuid());

  useEffect(() => {
    citationInteractionsRef.current = citationInteractions;
  }, [citationInteractions]);

  /* ── Thread management ── */
  const {
    currentThreadId,
    setCurrentThreadId,
    localThreadIds,
    loadThreads,
    createNewThread,
    ensureThreadExists,
  } = useChatThreads({ threadType: "private" });

  // Auto-create a fresh thread on mount
  useEffect(() => {
    if (!hasCreatedThread.current) {
      hasCreatedThread.current = true;
      createNewThread();
    }
  }, [createNewThread]);

  /* ── Streaming ── */
  const {
    chatEntry,
    setChatEntry,
    messages,
    sentHistory,
    showLoading,
    streamingText,
    isStreaming,
    streamingCitations,
    statusText,
    sendPayload,
    handleStop,
    handleRetry,
    handleToggleLike,
    handleToggleDislike,
    handleToggleCatalog,
  } = useChatStream({
    currentThreadId,
    setCurrentThreadId,
    createNewThread,
    ensureThreadExists,
    loadThreads,
    localThreadIds,
    speechMode: false,
    getReadyDocIds: () => [],
    clearAttachedFiles: () => {},
    threadType: "private",
    selectedModel: "",
    historyDepth: 5,
  });

  /* ── Source panel handlers ── */
  const handleSourceClick = useCallback(
    (source) => {
      const docId = source.doc_id;
      if (!docId) return;
      if (activeSourceId === docId && sourceOpen) return;

      setActiveSourceId(docId);

      // If already fetched, just open the panel
      const existing = sourceArticles.find((e) => e.id === docId);
      if (existing) {
        setSelectedSource(existing.article);
        setSourceError(null);
        setSourceLoading(false);
        setSourceOpen(true);
        return;
      }

      // Fetch the full article
      setSourceLoading(true);
      setSourceError(null);
      setSelectedSource(null);
      setSourceOpen(true);

      getDocIdData(docId)
        .then((response) => {
          const article = response?.data;
          if (!article) throw new Error("No data returned");

          const uid = userUuidRef.current;
          const existingInteraction = citationInteractionsRef.current[docId];
          const interactionState = existingInteraction || {
            like:
              typeof article.like === "boolean"
                ? article.like
                : isUserInArray(article.like, uid),
            dislike:
              typeof article.dislike === "boolean"
                ? article.dislike
                : isUserInArray(article.dislike, uid),
            catalog:
              typeof article.catalog === "boolean"
                ? article.catalog
                : isUserInArray(article.catalog, uid),
          };

          const enriched = {
            ...article,
            like: interactionState.like,
            dislike: interactionState.dislike,
            catalog: interactionState.catalog,
          };

          if (!existingInteraction) {
            setCitationInteractions((prev) => ({
              ...prev,
              [docId]: interactionState,
            }));
          }

          setSourceArticles((prev) => [
            ...prev,
            { id: docId, article: enriched },
          ]);
          setSelectedSource(enriched);
          setSourceLoading(false);
        })
        .catch((err) => {
          console.error("Source fetch error:", err);
          setSourceLoading(false);
          setSourceError(source);
        });
    },
    [sourceArticles, activeSourceId, sourceOpen],
  );

  const handleSourceInteract = useCallback(
    (cardId, docId, { like, dislike, catalog }) => {
      setCitationInteractions((prev) => ({
        ...prev,
        [docId]: { like, dislike, catalog },
      }));
      setSourceArticles((prev) =>
        prev.map((e) =>
          e.id === docId
            ? { ...e, article: { ...e.article, like, dislike, catalog } }
            : e,
        ),
      );
      // Keep the panel article in sync
      setSelectedSource((prev) =>
        prev?.document_id === docId
          ? { ...prev, like, dislike, catalog }
          : prev,
      );
    },
    [],
  );

  const closeSourcePanel = useCallback(() => {
    setSourceOpen(false);
    // Clear after transition
    setTimeout(() => {
      setSelectedSource(null);
      setSourceError(null);
      setActiveSourceId(null);
    }, 300);
  }, []);

  /* ── Render the Q+A pairs ── */
  const displayThread = renderPrivateThread({
    messages,
    onToggleLike: handleToggleLike,
    onToggleDislike: handleToggleDislike,
    onToggleCatalog: handleToggleCatalog,
    onOpenSidebar: () => {},
    onRetry: handleRetry,
    onSourceClick: handleSourceClick,
    isRightOpen: true,
    isStreaming,
    activeSourceId,
    citationInteractions,
    onOpenInEditor: () => {},
  });

  /* ── Send wrapper: always passes the fixed agent ── */
  const handleSend = useCallback(
    (msg) => {
      sendPayload(msg, agent, currentThreadId);
    },
    [sendPayload, agent, currentThreadId],
  );

  const showStreamingOutput = isStreaming && !!streamingText;

  return (
    <div className="relative flex flex-col h-full bg-mainBg text-mainText font-poppins overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-mainBorder shrink-0">
        <div className="w-7 h-7 rounded-full bg-velBlue/20 flex items-center justify-center">
          <span className="text-velBlue text-[11px] font-semibold">
            {agent.charAt(0)}
          </span>
        </div>
        <p className="text-sm font-medium text-mainText">{agent} Agent</p>
      </div>

      {/* Thread area */}
      <div className="flex-1 min-h-0">
        <ChatThread
          displayThisThread={displayThread}
          streamingText={streamingText}
          showLoading={showLoading}
          showStreamingOutput={showStreamingOutput}
          streamingCitations={streamingCitations}
          statusText={statusText}
          messageCount={messages.length}
          isTeamChat={false}
        />
      </div>

      {/* Input */}
      <div className="shrink-0">
        <ChatEntry
          compact
          chatType="private"
          chatEntry={chatEntry}
          setChatEntry={setChatEntry}
          sendPayload={handleSend}
          selectedAgent={agent}
          currentThreadId={currentThreadId}
          sentHistory={sentHistory}
          messages={messages}
          isStreaming={isStreaming}
          onStop={handleStop}
        />
      </div>

      {/* ── Source overlay panel ── */}
      {/* Backdrop: the visible chat sliver that dismisses the panel */}
      <div
        className={[
          "absolute inset-0 z-20 transition-opacity duration-300",
          sourceOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none",
        ].join(" ")}
        onClick={closeSourcePanel}
        style={{ background: "transparent" }}
      />

      {/* Sliding panel */}
      <div
        className={[
          "absolute top-0 right-0 bottom-0 z-30 flex flex-col bg-mainBg border-l border-mainBorder shadow-xl shadow-mainShadow transition-transform duration-300 ease-in-out",
          sourceOpen ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
        style={{ width: "85%" }}
      >
        {/* Panel content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {sourceLoading && (
            <div className="flex items-center justify-center gap-2 p-6 text-mutedText">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-xs font-normal">Loading source…</span>
            </div>
          )}

          {sourceError && !sourceLoading && (
            <div className="flex flex-col items-center justify-center gap-3 p-6 text-center h-full">
              <div className="rounded-full bg-logoutColor/10 p-3">
                <X className="h-5 w-5 text-logoutColor" />
              </div>
              <p className="text-xs font-medium text-mainText">
                Unable to load source
              </p>
              <p className="text-[11px] text-mutedText max-w-[200px]">
                {sourceError.title
                  ? `"${sourceError.title.length > 50 ? sourceError.title.slice(0, 49) + "…" : sourceError.title}"`
                  : "This document"}{" "}
                could not be retrieved.
              </p>
              <button
                onClick={() => handleSourceClick(sourceError)}
                className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-normal bg-surface/80 border border-inputBorder text-mutedText hover:text-mainText hover:border-surfaceBorder transition-colors cursor-pointer"
              >
                <Loader2 className="h-3 w-3" style={{ animation: "none" }} />
                Retry
              </button>
            </div>
          )}

          {selectedSource && !sourceLoading && !sourceError && (
            <ArticleDetailPanel
              article={selectedSource}
              cardId={selectedSource.document_id}
              onInteract={handleSourceInteract}
              onClose={closeSourcePanel}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SidebarAgentChat;
